'use client';

import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  MouseEventHandler,
} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { composeImageUrl } from '@/utils/imageAsset';
import { ImageType } from '@/utils/common.types';

interface Props {
  gallery: ImageType[];
  isDigitalModel?: boolean;
  modelSlug?: string | null;
  includeThumbnails?: boolean;
  isGalleryGrid?: boolean;
}

const Carousel = ({
  gallery,
  isDigitalModel = false,
  modelSlug = null,
  includeThumbnails = true,
  isGalleryGrid = false,
}: Props) => {
  const [imageIndex, setImageIndex] = useState(0);
  const [, setScollSnap] = useState([]);
  const [sliderRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [thumbnailRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const onPrevClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollPrev();
      setImageIndex(index === 0 ? gallery?.length - 1 : index - 1);
    },
    [emblaApi]
  );

  const onNextClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollNext();
      setImageIndex(index === gallery?.length - 1 ? 0 : index + 1);
    },
    [emblaApi]
  );

  const onNavClick = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      setImageIndex(index);
    },
    [emblaApi]
  );

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaApi || !emblaThumbsApi) return;
      emblaApi.scrollTo(index);
      setImageIndex(index);
    },
    [emblaApi, emblaThumbsApi]
  );

  const onInit = useCallback((emblaApi) => {
    setScollSnap(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    if (!emblaApi) return;
    setImageIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onThumbsSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    setImageIndex(emblaApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
  }, [emblaApi, emblaThumbsApi, setImageIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    onThumbsSelect();

    emblaApi.on('select', onThumbsSelect).on('reInit', onThumbsSelect);
  }, [emblaApi, onThumbsSelect]);

  const generateImage = (image: ImageType, w: number) => (
    <Image
      src={composeImageUrl({ image, w })}
      alt={image?.title || 'Carousel Image'}
      height={300}
      width={425}
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      className={classNames('w-full flex-[0_0_110%] bg-black-standard', {
        ['-mb-[5px] h-[200px] cursor-pointer object-contain [@media(min-width:600px)]:object-cover']:
          isGalleryGrid,
        ['object-contain md:h-[400px] lg:h-[600px]']: !isGalleryGrid,
      })}
    />
  );

  const generateArrowContainer = (
    onClick: MouseEventHandler<HTMLDivElement>
  ) => {
    return (
      <div
        className="relative h-7 w-7 cursor-pointer rounded-[50%] border-0 bg-white !p-[5.75px] text-center text-black-standard shadow-component hover:bg-clayton-blue hover:text-white"
        onClick={onClick}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.35355 4.35355C9.54882 4.15829 9.54882 3.84171 9.35355 3.64645C9.15829 3.45118 8.84171 3.45118 8.64645 3.64645L4.64645 7.64645C4.45118 7.84171 4.45118 8.15829 4.64645 8.35355L8.64645 12.3536C8.84171 12.5488 9.15829 12.5488 9.35355 12.3536C9.54882 12.1583 9.54882 11.8417 9.35355 11.6464L5.70711 8L9.35355 4.35355Z"
            style={{ fill: 'currentColor' }}
          />
        </svg>
      </div>
    );
  };

  return (
    gallery &&
    gallery?.length > 0 && (
      <section>
        <div>
          <div className="group relative my-2.5 overflow-hidden">
            <div className="overflow-hidden" ref={sliderRef}>
              <div className="flex">
                {gallery?.map((image, key) =>
                  isGalleryGrid && isDigitalModel && modelSlug ? (
                    <Link
                      key={key}
                      href={modelSlug}
                      target="_self"
                      className="w-full flex-[0_0_110%] bg-black-standard"
                    >
                      {generateImage(image, 400)}
                    </Link>
                  ) : (
                    <Fragment key={key}>{generateImage(image, 800)}</Fragment>
                  )
                )}
              </div>
            </div>
            <div className="absolute left-0 top-1/2 group-hover:translate-x-5 group-hover:transition-[all_0.2s_ease_0]">
              {generateArrowContainer(() => onPrevClick(imageIndex))}
            </div>
            <div className="absolute right-0 top-1/2 rotate-180 group-hover:-translate-x-5 group-hover:transition-[all_0.2s_ease_0.2s]">
              {generateArrowContainer(() => onNextClick(imageIndex))}
            </div>
            <div className="absolute bottom-[7.5%] flex w-full flex-row justify-center">
              {gallery?.map((_, key) => (
                <span
                  key={key}
                  className={classNames(
                    'mx-1.5 my-0 h-[5px] w-[5px] cursor-pointer rounded-[50%] bg-grey-3 shadow-component md:h-[7px] md:w-[7px]',
                    {
                      ['bg-white']: key === imageIndex,
                    }
                  )}
                  onClick={() => onNavClick(key)}
                />
              ))}
            </div>
          </div>
          {includeThumbnails && (
            <div>
              <div className="overflow-hidden" ref={thumbnailRef}>
                <div className="flex flex-row">
                  {gallery.map((thumb, key) => (
                    <Image
                      key={key}
                      src={composeImageUrl({ image: thumb, w: 250 })}
                      alt={thumb?.title || `Thumbnail ${key}`}
                      height={75}
                      width={175}
                      className={classNames(
                        'mr-[0.8rem] cursor-pointer rounded-[4px] object-cover',
                        {
                          ['border-[1.5px] border-solid border-black-standard']:
                            key === imageIndex,
                        }
                      )}
                      onClick={() => onThumbClick(key)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    )
  );
};

export default Carousel;
